import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Heart, MousePointerClick, ArrowUpRight, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { timeAgo, initials } from "@/lib/format";
import { trackHypeClick } from "@/lib/api";

export const HypeRow = ({ row, featured = false }) => {
    const navigate = useNavigate();
    const [clicking, setClicking] = useState(false);
    const seeded = !!row.seeded;

    const openTarget = async () => {
        setClicking(true);
        try {
            const res = await trackHypeClick(row.id);
            window.open(res?.target || row.url, "_blank", "noopener,noreferrer");
        } catch {
            window.open(row.url, "_blank", "noopener,noreferrer");
        } finally {
            setClicking(false);
        }
    };

    const boost = () => {
        if (seeded) {
            navigate(`/list?url=${encodeURIComponent(row.url)}&handle=${encodeURIComponent(row.x_handle || "")}`);
            return;
        }
        window.open(row.boost_url, "_blank", "noopener,noreferrer");
    };

    if (featured) {
        return (
            <div
                data-testid={`hype-featured-${row.id}`}
                className="relative overflow-hidden rounded-2xl border border-brand/40 bg-card p-5 sm:p-6 glow-brand"
            >
                <div className="absolute inset-0 hero-wash opacity-70 pointer-events-none" />
                <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="grid place-items-center h-14 w-14 rounded-xl bg-brand text-primary-foreground font-heading text-2xl font-bold shrink-0">
                            1
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <button
                                    data-testid={`hype-name-${row.id}`}
                                    onClick={openTarget}
                                    disabled={clicking}
                                    className="font-heading text-xl sm:text-2xl font-bold truncate hover:text-brand transition-colors inline-flex items-center gap-1.5"
                                >
                                    {row.display_name}
                                    <ExternalLink className="h-4 w-4 opacity-60" />
                                </button>
                                <Badge className="bg-brand/20 text-brand border-brand/30 hover:bg-brand/20">
                                    <Crown className="h-3 w-3 mr-1" /> Crowd #1
                                </Badge>
                                {seeded ? (
                                    <Badge variant="secondary">Unclaimed</Badge>
                                ) : null}
                            </div>
                            {row.specialty ? (
                                <Badge variant="secondary" className="mt-1.5 mr-2">{row.specialty}</Badge>
                            ) : null}
                            {row.description ? (
                                <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{row.description}</p>
                            ) : null}
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1 tabular">
                                    <MousePointerClick className="h-3.5 w-3.5" /> {row.clicks} clicks
                                </span>
                                <a
                                    href={row.tweet_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-foreground"
                                >
                                    @{row.x_handle}
                                </a>
                                <span>· {timeAgo(row.created_at)}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex sm:flex-col items-end justify-between gap-3 shrink-0">
                        <div className="text-right">
                            <div className="font-heading text-3xl font-bold tabular text-brand inline-flex items-center gap-1.5">
                                <Heart className="h-6 w-6" /> {row.like_count}
                            </div>
                            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">likes</div>
                        </div>
                        <Button
                            data-testid={`hype-boost-${row.id}`}
                            onClick={boost}
                            className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                        >
                            {seeded ? (
                                <>Claim with a tweet <ArrowUpRight className="h-4 w-4 ml-1" /></>
                            ) : (
                                <>Keep them #1 <ArrowUpRight className="h-4 w-4 ml-1" /></>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid={`hype-row-${row.id}`}
            className="group flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-3 sm:px-4 hover:border-white/15 hover:bg-secondary/40 transition-colors"
        >
            <div className="grid place-items-center min-w-10 h-10 rounded-lg bg-secondary text-sm font-semibold tabular text-muted-foreground shrink-0">
                {row.rank}
            </div>
            <div className="grid place-items-center h-9 w-9 rounded-lg bg-surface-2 text-xs font-bold text-muted-foreground shrink-0">
                {initials(row.display_name)}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <button
                        data-testid={`hype-name-${row.id}`}
                        onClick={openTarget}
                        disabled={clicking}
                        className="font-medium truncate hover:text-brand transition-colors inline-flex items-center gap-1"
                    >
                        {row.display_name}
                        <ExternalLink className="h-3.5 w-3.5 opacity-50" />
                    </button>
                    {seeded ? (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">Unclaimed</Badge>
                    ) : null}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    {row.specialty ? (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 truncate max-w-[140px]">
                            {row.specialty}
                        </Badge>
                    ) : null}
                    <a
                        href={row.tweet_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-muted-foreground hover:text-foreground truncate"
                    >
                        @{row.x_handle}
                    </a>
                    <span className="text-xs text-muted-foreground">· {timeAgo(row.created_at)}</span>
                </div>
            </div>
            <div className="text-right shrink-0">
                <div className="font-heading font-semibold tabular inline-flex items-center gap-1">
                    <Heart className="h-3.5 w-3.5 text-heat" /> {row.like_count}
                </div>
            </div>
            <Button
                data-testid={`hype-boost-${row.id}`}
                size="sm"
                variant="outline"
                onClick={boost}
                className="shrink-0 border-brand/40 text-brand hover:bg-brand hover:text-primary-foreground font-semibold"
            >
                {seeded ? "Claim" : row.likes_to_pass > 0 ? `${row.likes_to_pass} to pass` : "Boost"}
            </Button>
        </div>
    );
};
